import { Component, ViewContainerRef } from "@angular/core";
import { ApiService } from "../../../services/api.service";
import { CommonModule } from "@angular/common";
import { ModalComponent } from "../../modal/modal.component";
import { UserDialogComponent } from "./user-dialog/user-dialog.component";
import { FormsModule } from "@angular/forms";
import { User } from "../../../models/user.interface";
import { ConfirmDialogComponent } from "../../confirm-dialog/confirm-dialog.component";
import { ToasterService } from "../../../services/toaster.service";
import { LoadingService } from "../../../services/loading.service";
import { NoDataFoundComponent } from "../../no-data-found/no-data-found.component";
import { PaginatedListDataResponse } from "../../../models/paginated-list-data-response";
import { SearchBarConfig, TableListRequest, Where } from "../../../models/table-list-request.interface";
import { forkJoin } from "rxjs";
import { ResizableModule } from "angular-resizable-element";
import { RightPanelComponent } from "../../right-panel/right-panel.component";
import { LoginHistoryComponent } from "../../login-history/login-history.component";
import { TosTableComponent } from "../../tos-table/tos-table.component";
import UserManagementTableConfig from "./config/userManagementTableConfig.json";
import { TableMenuOption, TableMeta } from "../../../models/table-meta.interface";
import { StatsData, StatsResponse } from "../../../models/stats-response.interface";
import userFilterConfig from "./config/userManagementFilterConfig.json";
import { RequiresPermissionDirective } from "../../../directives/evaluate-permission.directive";
import { PermissionConstants } from "../../../constants/permission-constants";
import { PermissionService } from "../../../services/permission.service";

@Component({
  selector: "app-user-management",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ResizableModule,
    TosTableComponent,
    RequiresPermissionDirective
  ],
  templateUrl: "./user-management.component.html",
  styleUrl: "./user-management.component.scss"
})
export class UserManagementComponent {
  //table
  tableLoaded: boolean = false;
  totalTablePages = 0;
  currentTablePage = 1;
  currentPage: number = 0;
  rowsPerPage: number = 15;
  rowsPerPageOptions: number[] = [];
  focusSearch: boolean = false;
  searchTerm: string = "";
  previousTerm: string = "";
  selectedItems: User[] = [];
  // deSelectedItems: User[] = [];
  sortColumn: string = "modifiedDate";
  sortDirection: "asc" | "desc" = "desc";
  allTableRecordsSelected: boolean = false;
  tableStats: StatsData = {
    total: 0,
    active: 0,
    inactive: 0
  };
  tableListData: PaginatedListDataResponse = {
    totalCount: 0,
    page: 0,
    limit: 0,
    records: []
  };
  tableListRequest: TableListRequest = {
    selectFields: [
      "id",
      "name",
      "email",
      "username",
      "mobile",
      "loginMode",
      "active",
      "isLocked",
      "isSuperAdmin",
      "roles",
      "modifiedDate",
      "modifiedBy"
    ],
    orderBy: {
      [this.sortColumn]: this.sortDirection
    },
    includeTotal: true,
    limit: this.rowsPerPage,
    page: this.currentPage
  };

  filterData: Where | null = null;
  rawFilterData: Where | null = null;
  searchFilterData!: Where | null;
  searchBarConfig: SearchBarConfig = {
    filters: [
      {
        name: "NAME_CONTAINS",
        alias: "name_contains"
      },
      {
        name: "EMAIL_CONTAINS",
        alias: "email_contains"
      }
    ],
    placeHolder: "Search by name or email"
  }

  // table menu
  openMenuIndex: number | null = null;
  menuOptions: TableMenuOption[] = [
    { label: "Edit", value: "edit", requiresPermission: { permissionIds: [PermissionConstants.USERS.EDIT] } },
    { label: "Mark as active", value: "active", condition: (record) => !record.active,requiresPermission: { permissionIds: [PermissionConstants.USERS.EDIT] } },
    { label: "Mark as inactive", value: "inactive", condition: (record) => record.active,requiresPermission: { permissionIds: [PermissionConstants.USERS.EDIT] } },
    { label: "Lock User", value: "lock", condition: (record) => !record.locked,requiresPermission: { permissionIds: [PermissionConstants.USERS.EDIT] } },
    { label: "Unlock User", value: "unlock", condition: (record) => record.locked ,requiresPermission: { permissionIds: [PermissionConstants.USERS.EDIT] }},
    // { label: "Grant Admin Access", value: "grantAdmin", condition: (record) => !record.isSuperAdmin },
    // { label: "Revoke Admin Access", value: "revokeAdmin", condition: (record) => record.isSuperAdmin },
    { label: "Send Welcome Mail", value: "welcomeMail",requiresPermission: { permissionIds: [PermissionConstants.USERS.EDIT] } },
    { label: "View Login History", value: "loginHistory" }
  ];
  tableMeta: TableMeta = UserManagementTableConfig;

  protected readonly PermissionConstants = PermissionConstants;
  protected readonly NoDataFoundComponent = NoDataFoundComponent;
  protected readonly userFilterConfig = userFilterConfig;

  constructor(
    private api: ApiService,
    private toasterService: ToasterService,
    private viewContainerRef: ViewContainerRef,
    private loadingService: LoadingService,
    private permissionService: PermissionService
  ) {
  }

  // init
  ngOnInit() {
    this.tableLoaded = false;
    this.tableMeta = UserManagementTableConfig;
    this.getTableListWithStats();
  }

  clearSelection() {
    // console.warn('clearing both selectedItems & selectedItems !');
    this.selectedItems = [];
    // this.deSelectedItems = [];
  }

  // fetch table data and stats
  getTableListWithStats(): void {
    this.loadingService.show('inline');
    const getTableStatsApiCall = this.api.getUsersStats();
    const getTableListApiCall = this.api.getUsers(this.tableListRequest);
    forkJoin([getTableStatsApiCall, getTableListApiCall]).subscribe({
      next: ([tableStats, tableList]) => {
        this.tableStats = tableStats.data;
        this.tableListData = tableList.data;
        // this.patchTableListWithSerialNumbers();
        // this.calculateTotalPages();
        // this.generateRowsPerPageOptions(tableList.data.totalCount);
        this.tableLoaded = true;
        this.loadingService.hide();
      },
      error: (err) => {
        console.error(err);
        // this.toasterService.error(err.message);
        this.loadingService.hide();
      }
    });
  }

  handleOptionSelected(event: any) {
    const { record: user, actionName } = event;
    if (actionName === "loginHistory") {
      this.viewLoginHistory(user);
      return;
    }

    if (actionName === "edit") {
      // no confirmation is required
      const modalRef = this.viewContainerRef.createComponent(ModalComponent);
      modalRef.instance.title = "Edit User";
      const userDialogRef = modalRef.instance.loadComponent(
        UserDialogComponent,
        user
      );
      userDialogRef.setInput("userEditingEnabled", true);
      userDialogRef.setInput("userId", user.id);
      userDialogRef.setInput("userObj", user);
      modalRef.instance.close.subscribe((res: any) => {
        if (res && res.refresh) {
          this.refreshTableListWithStats();
        }
        modalRef.destroy();
      });
    } else {
      // need to confirm before executing the action
      let actionTitle = "Confirmation";
      let message = "Are you sure ?";
      let successMsg = "Done !";
      let flag = false;
      const userId = user.id;

      switch (actionName) {
        case "active":
        case "inactive":
          if (actionName === "active") {
            flag = true;
          }
          actionTitle = `${flag ? "Activate" : "Inactivate"} User Confirmation`;
          message = `Are you sure to ${flag ? "activate" : "inactivate"} ${
            user.name
          }'s account ?`;
          successMsg = `Successfully ${flag ? "activated" : "inactived"} ${
            user.name
          }'s account`;
          break;
        case "lock":
        case "unlock":
          if (actionName === "lock") {
            flag = true;
          }
          actionTitle = `${flag ? "Lock" : "Unlock"} User Account Confirmation`;
          message = `Are you sure to ${flag ? "lock" : "unlock"} ${
            user.name
          }'s account ?`;
          successMsg = `Successfully ${flag ? "locked" : "unlocked"} ${
            user.name
          }'s account`;
          break;
        case "grantAdmin":
        case "revokeAdmin":
          if (actionName === "grantAdmin") {
            flag = true;
          }
          actionTitle = `${
            flag ? "Grant" : "Revoke"
          } Admin Access Confirmation`;
          message = `Are you sure to ${
            flag ? "grant" : "revoke"
          } admin access ${flag ? "to" : "from"} ${user.name}'s account ?`;
          successMsg = `Successfully ${
            flag ? "granted" : "revoked"
          } admin access ${flag ? "to" : "from"} ${user.name}'s account`;
          break;
        case "welcomeMail":
          actionTitle = `Welcome mail Confirmation`;
          message = `Are you sure to send welcome mail to ${user.username} ?`;
          successMsg = `Successfully sent welcome mail to ${user.username}`;
          break;
        default:
          console.error("unknown actionName");
          break;
      }
      const modalRef = this.viewContainerRef.createComponent(ModalComponent);
      modalRef.instance.title = actionTitle;
      const confirmDialogRef = modalRef.instance.loadComponent(
        ConfirmDialogComponent
      );
      confirmDialogRef.instance.message = message;
      confirmDialogRef.instance.confirm.subscribe(() => {
        this.menuAction(actionName, successMsg, flag, userId!);
        modalRef.destroy();
      });
      confirmDialogRef.instance.cancel.subscribe(() => {
        modalRef.destroy();
      });
      modalRef.instance.close.subscribe(() => modalRef.destroy());
    }
  }

  menuAction(
    actionName: string,
    successMsg: string,
    flag: boolean,
    userId: string
  ) {
    switch (actionName) {
      case "active":
      case "inactive":
        this.api.setUserActiveStatus(flag, [userId]).subscribe({
          next: () => {
            this.toasterService.success(successMsg);
            this.refreshTableListWithStats();
          },
          error: (err) => {
            console.error(err);
            this.toasterService.error(err.error.errorDesc || "Unknown Error !");
          }
        });
        break;
      case "lock":
      case "unlock":
        this.api.setUserLock(flag, [userId]).subscribe({
          next: () => {
            this.toasterService.success(successMsg);
            this.refreshTableList();
          },
          error: (err) => {
            console.error(err);
            this.toasterService.error(err.error.errorDesc || "Unknown Error !");
          }
        });
        break;

      case "grantAdmin":
      case "revokeAdmin":
        this.api.setAdminAccessStatus(flag, userId).subscribe({
          next: () => {
            this.toasterService.success(successMsg);
            this.refreshTableList();
          },
          error: (err) => {
            console.error(err);
            this.toasterService.error(err.error.errorDesc || "Unknown Error !");
          }
        });
        break;
      case "welcomeMail":
        this.api.sendWelcomeMail(userId).subscribe({
          next: () => {
            this.toasterService.success(successMsg);
          },
          error: (err) => {
            console.error(err);
            this.toasterService.error(err.error.errorDesc || "Unknown Error !");
          }
        });
        break;
      default:
        console.error("unknown actionName");
        break;
    }
  }

  bulkAction(actionName: string, successMsg: string) {
    const selectedIds = this.selectedItems.map((user: User) => {
      return user.id!;
    });
    switch (actionName) {
      case "setActive":
        this.api.setUserActiveStatus(true, selectedIds).subscribe({
          next: () => {
            this.clearSelection();
            this.toasterService.success(successMsg);
            this.refreshTableListWithStats();
          },
          error: (err) => {
            console.error(err);
            this.toasterService.error(err.error.errorDesc || "Unknown Error !");
          }
        });
        break;
      case "setInActive":
        this.api.setUserActiveStatus(false, selectedIds).subscribe({
          next: () => {
            this.clearSelection();
            this.toasterService.success(successMsg);
            this.refreshTableListWithStats();
          },
          error: (err) => {
            console.error(err);
            this.toasterService.error(err.error.errorDesc || "Unknown Error !");
          }
        });
        break;
      case "unlock":
        this.api.setUserLock(false, selectedIds).subscribe({
          next: () => {
            this.clearSelection();
            this.toasterService.success(successMsg);
            this.refreshTableList();
          },
          error: (err) => {
            console.error(err);
            this.toasterService.error(err.error.errorDesc || "Unknown Error !");
          }
        });
        break;
      default:
        console.error("unknown actionName");
        break;
    }
  }

  getTableStats() {
    this.api.getUsersStats().subscribe({
      next: (res: StatsResponse) => {
        this.tableStats = res.data;
      },
      error: (err) => {
        console.error(err);
      }
    });
  }

  getTableList() {
    this.loadingService.show('inline')
    this.api.getUsers(this.tableListRequest).subscribe({
      next: (res) => {
        this.tableListData = res.data;
        this.loadingService.hide()
        // this.patchTableListWithSerialNumbers();
        // this.calculateTotalPages();
        // this.generateRowsPerPageOptions(res.data.totalCount);
      },
      error: (err) => {
        console.error(err);
        this.loadingService.hide()
      }
    });
  }


  refreshTableList() {
    this.getTableList();
  }

  refreshTableListWithStats() {
    this.getTableList();
    this.getTableStats();
  }

  addNewRecord() {
    const modalRef = this.viewContainerRef.createComponent(ModalComponent);
    modalRef.instance.title = "Create New User";
    const userDialogRef = modalRef.instance.loadComponent(UserDialogComponent);
    modalRef.instance.close.subscribe((res: any) => {
      if (res && res.refresh) {
        this.refreshTableListWithStats();
      }
      modalRef.destroy();
    });
  }

  private viewLoginHistory(user: User) {
    const rightPanelRef = this.viewContainerRef.createComponent(RightPanelComponent);
    rightPanelRef.instance.title = "Login History";

    const loginHistoryRef = rightPanelRef.instance.loadComponent(LoginHistoryComponent, { userId: user.id });

    rightPanelRef.instance.close.subscribe((res: any) => {
      if (res && res.refresh) {
        this.refreshTableListWithStats();
      }
      rightPanelRef.destroy();
    });
  }
}
